import { Capacitor } from '@capacitor/core';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { FileOpener } from '@capawesome-team/capacitor-file-opener';

/**
 * Device-side file handling for native mobile.
 *
 * iOS: files the user taps to view are written to a scratch folder in the OS cache directory,
 * handed to the OS viewer, and pruned on app start. They used to go to Directory.Documents and
 * were never deleted - on iOS that folder is invisible to the user, backed up to iCloud and
 * counted as app data, so every opened attachment (plus a new timestamped copy per chat re-open)
 * piled up there.
 *
 * Android: files still go to Directory.Documents, the user's public Documents folder, so opened
 * attachments stay available in their Files app. It doesn't count toward the app's storage, and
 * files overwrite by name, so re-opening an attachment doesn't add copies.
 */

const OPENED_FILES_DIR = 'opened-files';
const OPENED_FILE_MAX_AGE_MS = 3 * 24 * 60 * 60 * 1000;
/** Legacy iOS Documents files must be at least this old - anything in active use is newer. */
const LEGACY_DOCUMENTS_MIN_AGE_MS = 24 * 60 * 60 * 1000;

/**
 * Loose files in iOS Documents that belong to someone else and must never be removed:
 * - hidden/system files (".DS_Store", ".com.apple.*")
 * - Capgo's in-progress OTA download (package_<id>.tmp) and its resume marker (update_<id>.dat).
 *   Deleting these mid-download corrupts the update; Capgo prunes its own stale ones after 1 hour.
 */
function isProtectedDocumentsFile(name: string): boolean {
  return name.startsWith('.')
    || (name.startsWith('package_') && name.endsWith('.tmp'))
    || (name.startsWith('update_') && name.endsWith('.dat'));
}

/** Path separators and characters the iOS/Android file systems reject. */
function safeFileName(fileName: string): string {
  const cleaned = (fileName || '').replace(/[\\/:*?"<>|\u0000-\u001f]/g, '_').trim();
  return cleaned || 'file';
}

/**
 * Writes base64 file data to the device and opens it in the OS viewer. Returns false when no app
 * on the device can open it; throws only when the file can't be written.
 */
export async function openFileOnDevice(base64: string, fileName: string, mimeType?: string): Promise<boolean> {
  const name = safeFileName(fileName);
  const isAndroid = Capacitor.getPlatform() === 'android';
  const path = isAndroid ? name : `${OPENED_FILES_DIR}/${name}`;
  const directory = isAndroid ? Directory.Documents : Directory.Cache;

  await Filesystem.writeFile({ data: base64, path, directory, recursive: !isAndroid });
  const { uri } = await Filesystem.getUri({ path, directory });

  try {
    await FileOpener.openFile({ path: uri, mimeType: mimeType || undefined });
    return true;
  } catch (error) {
    console.error('Unable to open file:', name, mimeType, error);
    return false;
  }
}

/**
 * Writes base64 data into Documents without opening it ("Download all"). On Android that's the
 * user's public folder; same-named files are overwritten, as before.
 */
export async function saveFileToDevice(base64: string, fileName: string): Promise<void> {
  await Filesystem.writeFile({ data: base64, path: safeFileName(fileName), directory: Directory.Documents });
}

/**
 * Deletes opened-file copies older than a few days. On iOS it also clears the files earlier app
 * versions left in Documents: the app doesn't expose that folder in the Files app, so nothing
 * there was ever reachable by the user. Only loose, day-old files are removed there - plugin
 * sub-folders (e.g. the camera's videos folder) and isProtectedDocumentsFile names are skipped.
 * Android's Documents is the user's public folder and is never touched. Best-effort: every
 * failure is caught, and anything missed is retried on the next launch.
 */
export async function cleanupDeviceFiles(): Promise<void> {
  try {
    if (!Capacitor.isNativePlatform()) return;

    const now = Date.now();
    await deleteFilesOlderThan(OPENED_FILES_DIR, Directory.Cache, now - OPENED_FILE_MAX_AGE_MS);

    if (Capacitor.getPlatform() === 'ios') {
      await deleteFilesOlderThan('', Directory.Documents, now - LEGACY_DOCUMENTS_MIN_AGE_MS, isProtectedDocumentsFile);
    }
  } catch (error) {
    console.warn('Device file cleanup failed:', error);
  }
}

async function deleteFilesOlderThan(
  path: string,
  directory: Directory,
  cutoff: number,
  isProtected: (name: string) => boolean = () => false,
): Promise<void> {
  let files;
  try {
    ({ files } = await Filesystem.readdir({ path, directory }));
  } catch {
    // Folder doesn't exist yet, or (iOS) a file vanished mid-listing - try again next launch.
    return;
  }

  for (const file of files) {
    // An unknown date counts as "new" so the file is never deleted on a guess. That covers a
    // missing value and 0, which Android reports when it can't read the date.
    if (!(file.mtime > 0)) continue;

    // mtime is documented as milliseconds; a value that small can only be seconds (ms values
    // passed 1e11 in 1973), so normalise rather than treat every file as decades old.
    const mtimeMs = file.mtime < 1e11 ? file.mtime * 1000 : file.mtime;

    // Only loose files: anything a plugin keeps in its own sub-folder is left alone.
    if (file.type !== 'file' || mtimeMs >= cutoff || isProtected(file.name)) continue;
    try {
      await Filesystem.deleteFile({ path: path ? `${path}/${file.name}` : file.name, directory });
    } catch (error) {
      console.warn('File cleanup failed for', file.name, error);
    }
  }
}

export function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    // result is a data URL ("data:<type>;base64,...") - strip the prefix.
    reader.onload = () => resolve((reader.result as string).split(',')[1]);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}
